import { siteId as defaultSiteId } from 'src/config'
import Model from 'src/models/model'
import { prependWithConsumerRoot, storeLastVisitedSiteId, switchSite } from 'src/router/utils'

/**
 * Something about our app is causing each Router before/after guard to fire
 * twice, causing weird unintended behavior when pushing/popping off the browser
 * history stack. IDK man, I couldn't figure it out. To work around this, I am
 * using this Set to ensure only one instance of each guard is running per navigation.
 */
export const CURRENTLY_PROCESSING_GUARDS = new Set()

/**
 * @typedef {import('vue-router').NavigationGuard} NavigationGuard
 * @typedef {import('vue-router').NavigationHookAfter} NavigationHookAfter
 */

/**
 * These route guards are required, in this order, as the first or second
 * route guards in a sequence, to enable same-hostname site sharing.
 * @type {Readonly<NavigationGuard[]>}
 */
export const DEFAULT_SHARED_DOMAIN_SITE_GUARDS = Object.freeze([
  maybeSwitchToRootPathSite,
  ensurePathStartsWithNonRootPathSitePrefix,
  ensureSwitchedToSiteRequiredByRoute
])

/** Set to true after the very first consumer app navigation is fully resolved. */
let hasResolvedRedirectToPreferredOrLastSite = false
/**
 * This guard is intended to only be included as the first guard of the first route
 * a user enters the consumer app through, currently the "consumer:root" route.
 *
 * It enables automatically redirecting the user to their preferred or last visited site,
 * if any, when they first enter the consumer app.
 * @type {NavigationGuard}
 */
export async function redirectToPreferredOrLastSiteAtStart (to, _from, next) {
  if (CURRENTLY_PROCESSING_GUARDS.has('redirectToPreferredOrLastSiteAtStart')) {
    next()
    return
  }
  CURRENTLY_PROCESSING_GUARDS.add('redirectToPreferredOrLastSiteAtStart')

  await Model.ensureBooted()

  if (hasResolvedRedirectToPreferredOrLastSite) {
    next()
    return
  } else if (to.query.primary === 'true') {
    // The user is explicitly requesting to switch to a page belonging to the root path site.
    // We let the `maybeSwitchToRootPathSite` guard handle this case.
    next()
    return
  }

  hasResolvedRedirectToPreferredOrLastSite = true

  const currentSite = Model.configuration.currentSite
  let didSwitchSite = false

  // TODO INDY-7055: User preferred site, if any, should take precedence over last visited site here.

  const lastVisitedSiteId = Model.context.userOrDeviceLastVisitedSiteId
  if (_.isFinite(lastVisitedSiteId) && lastVisitedSiteId !== currentSite.id) {
    if (_.includes(currentSite.sharedHostnameSiteIds, lastVisitedSiteId)) {
      await switchSite(lastVisitedSiteId)
      didSwitchSite = true
    } else if (currentSite.crossDomainHostnamesBySiteId[lastVisitedSiteId]) {
      window.location.href = currentSite.crossDomainHostnamesBySiteId[lastVisitedSiteId]
      return
    }
  }

  if (didSwitchSite) {
    const homePage = Model.configuration.publishedPages.homePage
    if (homePage?.slug) {
      next({
        name: `consumer:site-${Model.configuration.currentSite.id}:home-page`,
        params: { slug: homePage.slug },
        query: { ...to.query },
        hash: to.hash
      })
      return
    }
  }

  next()
}

/**
 * This is only necessary if we want to support correctly switching back to the
 * root path ("/") site when the user backtracks in the following scenario:
 *
 * 1. A user lands on primary/root site. Example: "/home"
 * 2. The user navigates to a shared domain site. Example: "/other-site/home"
 * 3. The user clicks browser "back" button. Typical browser behavior would mean
 *    the user should be switched back to the primary/root site page of "/home".
 * 4. Continuing back and forth in history should also correctly toggle to/from the primary/root site.
 *
 * We can't rely on route guards `to/from.meta.requiredSiteId` or path values to determine
 * whether to switch back the primary site because our complex routing rules often
 * causes the attempted "/some-root-path" to actually match a non-primary/root site route.
 * So we use the native `history.state` to store this extra bit of information that
 * is only read when this scenario could occur.
 */
const HISTORY_STATE_WAS_ROOT_SITE_KEY = 'indyWasRootSite'

/**
 * This is intended for the Consumer app, for storing the most recently
 * visited site ID after each navigation is fully resolved.
 * @type {NavigationHookAfter}
 */
export async function storeLastVisitedSiteAfterGuard (to, _from) {
  await Model.ensureBooted()

  // If the user skipped hitting the "consumer:home-page" route first, we set this
  // to prevent a redirect on the next "consumer:home-page" route navigation.
  hasResolvedRedirectToPreferredOrLastSite = true

  const currentSite = Model.configuration.currentSite
  storeLastVisitedSiteId(currentSite.id)

  // The following is only used for supporting specific back/forward navigation scenarios.
  // See comments on HISTORY_STATE_WAS_ROOT_SITE_KEY.
  const newHistoryState = { ...(history.state || {}), [HISTORY_STATE_WAS_ROOT_SITE_KEY]: currentSite.rootPath === '/' }
  history.replaceState(newHistoryState, '', to.fullPath)

  const currentUser = Model.context.currentUser
  if (currentUser?.isRemote) {
    await currentUser.updateLastVisitedSiteId(currentSite.id)
  }
}

/**
 * This beforeEnter route guard allows switching to the root site and path ('/') when
 * the 'primary=true' query param is present, or when going back/forward in browser
 * history to a route that belonged to the root path site.
 *
 * When deliberately navigating to a root path site, we need to switch to the root site
 * before the `ensurePathStartsWithNonRootPathSitePrefix` guard so that it doesn't try to prepend
 * the path with a non-root site path.
 *
 * This should be among the first route guards, and should only be preceded by guards that
 * are intended to redirect away from the app initial homepage.
 * @type {NavigationGuard}
 */
export async function maybeSwitchToRootPathSite (to, from, next) {
  if (CURRENTLY_PROCESSING_GUARDS.has('maybeSwitchToRootPathSite')) {
    next()
    return
  }
  CURRENTLY_PROCESSING_GUARDS.add('maybeSwitchToRootPathSite')

  // Guards in a sequence must share the same ensureBooted awaits
  // to ensure proper execution order.
  await Model.ensureBooted()
  const currentSite = Model.configuration.currentSite
  const sharedHostnameSiteIdsByPath = currentSite.sharedHostnameSiteIdsByPath
  const rootPathSiteId = sharedHostnameSiteIdsByPath['/'] || (currentSite.rootPath === '/' ? currentSite.id : parseInt(defaultSiteId))

  if (currentSite.id === rootPathSiteId) {
    // User is already on the root path site. No-op.
    next()
  } else if (to.query.primary === 'true') {
    // The URL is explicitly requesting to switch to the root path site.
    await switchSite(rootPathSiteId)
    next({ path: to.path, query: _.omit(to.query, 'primary'), hash: to.hash })
  } else if (_.isNumber(from.meta.requiredSiteId) && from.meta.requiredSiteId !== rootPathSiteId && _.get(history, `state.${HISTORY_STATE_WAS_ROOT_SITE_KEY}`) === true) {
    // The is only used for supporting specific back/forward navigation scenarios
    // where the user is backing/forwarding from a non-root site. See comments on HISTORY_STATE_WAS_ROOT_SITE_KEY.
    await switchSite(rootPathSiteId)
    next()
  } else {
    // No need to force a switch to the root path site in this guard. Subsequent
    // route guards handle possibly switching to another non-root site.
    next()
  }
}

/**
 * When supporting sharing URL domains across multiple sites via URL (sub)paths, this
 * beforeEnter guard should be set before the site switching guard.
 *
 * Since most URL links up until this feature assumed a root path of '/', we need to
 * modify such URLs by prefixing them with the current site's root path. It was either
 * this or try to find every current and future URL in sites' content and replace them
 * with prefixed URLs.
 *
 * Example assuming current site is "site-1":
 *   '/home' -> '/site-1/home'
 *   '/site-2/home' -> '/site-1/home'
 *
 * The only exception is when the navigation includes the query param 'primary=true',
 * in which case we assume we want to redirect and switch to the root '/' path and site,
 * removing non-root site prefix if present. Example:
 *   '/site-1/home?primary=true' -> '/home'
 *
 * This is expected to by followed by the `ensureSwitchedToSiteRequiredByRoute` beforeEnter guard.
 * @type {NavigationGuard}
 */
export async function ensurePathStartsWithNonRootPathSitePrefix (to, _from, next) {
  if (CURRENTLY_PROCESSING_GUARDS.has('ensurePathStartsWithNonRootPathSitePrefix')) {
    next()
    return
  }
  CURRENTLY_PROCESSING_GUARDS.add('ensurePathStartsWithNonRootPathSitePrefix')

  await Model.ensureBooted()

  const currentSite = Model.configuration.currentSite
  if (currentSite.rootPath === '/') {
    // The current site is not nested under some subpath (e.g. '/site-1/'),
    // so we take the `to.path` at face value and proceed without modifying it.
    next()
    return
  }

  // At this point we know the current site is nested under some subpath (e.g. '/site-1/').
  // We assume the user is trying to navigate to a page within the current site, even if
  // the `to.path` may not have the site's subpath prefix. We make this assumption so we
  // don't have to worry about finding and prefixing all URLs in site content with the sites' subpath.

  const toPathHasSiteSubpathPrefix = _.some(currentSite.sharedHostnamePathMatchersBySiteId, (sitePathRegexp, _siteId) => {
    return sitePathRegexp.test(to.path)
  })
  if (toPathHasSiteSubpathPrefix) {
    // A `to.path` with a prefix of the curren, or sibling, site is acceptable as-is.
    // The next route guard will handle any necessary site switching if the path requires it.
    next()
    return
  }

  // At this point, we coerce the `to.path` to have the current site's subpath prefix since
  // we know the `to.path` does not have any of the sites' required subpath prefixes.
  let correctedPath
  if (currentSite.rootPath !== '/') {
    const requiredRootPath = prependWithConsumerRoot(currentSite.rootPath)
    if (to.path !== requiredRootPath && !_.startsWith(to.path, `${requiredRootPath}/`)) {
      correctedPath = to.path === '/' ? requiredRootPath : `${requiredRootPath}${to.path}`
    }
  }

  if (correctedPath) {
    next({ path: correctedPath, query: { ...to.query }, hash: to.hash })
  } else {
    next()
  }
}

/**
 * This is a beforeEnter route guard that can trigger switching the currentSite and
 * reloading all data when navigating to a (sub)path mapped to a specific site. The
 * target route's `meta.requiredSiteId` value is used to determine the site to switch to.
 * We assume the routes with the various site path prefixes have already been configured
 * with corresponding `meta: { requiredSiteId: <siteId> }` values.
 *
 * For example navigating from '/site-1/home' to '/site-2/home' would trigger a site switch
 * if not already switched.
 *
 * Any redirects to a different site's path should be performed before this guard.
 * @type {NavigationGuard}
 */
export async function ensureSwitchedToSiteRequiredByRoute (to, _from, next) {
  if (CURRENTLY_PROCESSING_GUARDS.has('ensureSwitchedToSiteRequiredByRoute')) {
    next()
    return
  }
  CURRENTLY_PROCESSING_GUARDS.add('ensureSwitchedToSiteRequiredByRoute')

  await Model.ensureBooted()
  const currentSiteId = Model.configuration.currentSite.id
  const requiredSiteId = to.meta.requiredSiteId

  if (requiredSiteId && currentSiteId && requiredSiteId !== currentSiteId) {
    await switchSite(requiredSiteId)
  }
  next()
}
